import React, { useState } from 'react';
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, Chip, Typography,
  IconButton, Tooltip, Button, CircularProgress, Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ContentCopy, CheckCircle, PauseCircleOutline, PlayCircleOutline,
  VerifiedUser, Business, MarkEmailRead,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { updateTenantStatus } from '../../services/tenants';
import MailSettingsDialog from './MailSettingsDialog';

const CHAT_DOMAIN = 'chat.alodesk.com.br';

const StatusChip = ({ status }) => {
  const isActive = status === 'active';
  return (
    <Chip
      size="small"
      label={isActive ? 'Ativa' : 'Suspensa'}
      color={isActive ? 'success' : 'default'}
      variant={isActive ? 'filled' : 'outlined'}
    />
  );
};

const TenantRow = ({ tenant, onChanged }) => {
  const theme = useTheme();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const chatUrl = `${CHAT_DOMAIN}/${tenant.slug}`;
  const isActive = tenant.status === 'active';

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(`https://${chatUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const toggleStatus = async () => {
    setBusy(true);
    try {
      const nextStatus = isActive ? 'suspended' : 'active';
      await updateTenantStatus(tenant.id, nextStatus);
      toast.success(
        nextStatus === 'active' ? `${tenant.name} reativada.` : `${tenant.name} suspensa.`,
        { position: toast.POSITION.BOTTOM_CENTER },
      );
      onChanged?.();
    } catch (error) {
      const msg = error?.response?.data?.error || 'Não foi possível alterar o status da empresa.';
      toast.error(msg, { position: toast.POSITION.BOTTOM_CENTER });
    } finally {
      setBusy(false);
    }
  };

  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{tenant.name}</Typography>
          {tenant.is_system && (
            <Tooltip title="Tenant do sistema — hospeda a própria plataforma, nunca pode ser suspenso">
              <Chip
                size="small"
                icon={<VerifiedUser sx={{ fontSize: 14 }} />}
                label="Sistema"
                color="primary"
                variant="outlined"
              />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: theme.palette.text.secondary }}>
          {tenant.slug}
        </Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
          >
            {chatUrl}
          </Typography>
          <Tooltip title={copied ? 'Copiado!' : 'Copiar URL pública do chat'}>
            <IconButton size="small" onClick={copyUrl}>
              {copied ? <CheckCircle fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell><StatusChip status={tenant.status} /></TableCell>
      <TableCell align="right">
        <Tooltip title="Caixa de e-mail — os e-mails não lidos viram chamados desta empresa">
          <IconButton size="small" onClick={() => setMailOpen(true)} sx={{ mr: 1 }}>
            <MarkEmailRead fontSize="small" />
          </IconButton>
        </Tooltip>

        <MailSettingsDialog
          open={mailOpen}
          tenant={tenant}
          onClose={() => setMailOpen(false)}
        />

        <Tooltip title={tenant.is_system ? 'Tenant do sistema não pode ser suspenso' : (isActive ? 'Suspender' : 'Reativar')}>
          <span>
            <Button
              size="small"
              variant="outlined"
              color={isActive ? 'error' : 'success'}
              disabled={busy || (isActive && tenant.is_system)}
              onClick={toggleStatus}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : (isActive ? <PauseCircleOutline /> : <PlayCircleOutline />)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {isActive ? 'Suspender' : 'Reativar'}
            </Button>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const TenantList = ({ tenants, loading, onChanged }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!tenants || tenants.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: `1px dashed ${theme.palette.divider}`, textAlign: 'center' }}>
        <Business sx={{ fontSize: 32, color: theme.palette.text.secondary, mb: 1 }} />
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Nenhuma empresa cadastrada ainda.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Slug</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>URL pública do chat</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tenants.map((tenant) => (
            <TenantRow key={tenant.id} tenant={tenant} onChanged={onChanged} />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default TenantList;
